import { css } from "@emotion/react";
import {
  Button,
  Loader,
  Modal,
  RadioButton,
} from "@moneyforward/mfx_biz_react_components";
import { color } from "@/styles/theme";
import { useEffect, useState } from "react";
import useRollbarUtil from "@/components/hooks/useRollbarUtil";
import { Address, useGetAddressLazyQuery } from "@/generated/graphql";

type Props = {
  postalCode: string;
  onClose: () => void;
  onConfirm: (address: Address) => void;
};

export const SelectAddressModal = ({
  postalCode,
  onClose,
  onConfirm,
}: Props) => {
  const { rollbar } = useRollbarUtil();
  const [loading, setLoading] = useState<boolean>(false);
  const [potentialAddresses, setPotentialAddresses] = useState<Address[]>([]);
  const [checkedAddressIndex, setCheckedAddressIndex] = useState<number | null>(
    null
  );
  const [isNotFoundError, setIsNotFoundError] = useState<boolean>(false);
  const [isApiError, setIsApiError] = useState<boolean>(false);
  const [notSelected, setNotSelected] = useState<boolean>(false);

  const [getAddressesQuery] = useGetAddressLazyQuery({
    onCompleted: (data) => {
      if (data?.addresses) {
        const addresses = data?.addresses;
        if (addresses.length === 1) {
          onConfirm(addresses[0]);
          setLoading(false);
          onClose();
        } else if (addresses.length > 0) {
          setPotentialAddresses(addresses);
          setLoading(false);
        } else {
          setIsNotFoundError(true);
          setLoading(false);
        }
      } else {
        setIsApiError(true);
        setLoading(false);
      }
    },
    onError: (error) => {
      rollbar.error("Failed to get address from kenall", error);
      setIsApiError(true);
      setLoading(false);
    },
  });

  useEffect(() => {
    if (!postalCode) {
      setIsNotFoundError(true);
      return;
    }
    setLoading(true);
    setCheckedAddressIndex(null);
    getAddressesQuery({
      variables: {
        postalCode: postalCode,
      },
    });
  }, [postalCode]);

  const errorModal = (message: string) => (
    <Modal
      css={modal}
      title={"住所の検索ができませんでした"}
      body={
        <div>
          <p css={{ whiteSpace: "pre-wrap" }}>{message}</p>
        </div>
      }
      zIndex={5}
      footerButtons={
        <Button type="button" variant="primary" onClick={onClose}>
          OK
        </Button>
      }
      onClose={onClose}
      backgroundColor={color.backgroundGray}
    />
  );

  const selectAddressModalBody = (
    <div
      css={{
        marginTop: 16,
        maxHeight: "calc(80vh - 128px)",
        overflow: "auto",
      }}
    >
      <div>
        <p>
          該当する住所を選択してください。
          <br />
          候補にない場合は郵便番号を変更して再度検索するか、住所を手入力してください。
        </p>
      </div>
      <div css={radioButtonList}>
        {potentialAddresses.map((ele, index) => {
          return (
            <RadioButton
              key={index}
              css={{ gap: 4, fontSize: "1.4rem", width: "100%" }}
              name="address"
              label={ele.prefectures + ele.municipality + ele.blockBuildingName}
              bgSize="md"
              checked={index === checkedAddressIndex}
              onChange={() => {
                setCheckedAddressIndex(index);
              }}
            />
          );
        })}
      </div>
      {notSelected && <p css={{ color: color.attention }}>選択してください</p>}
    </div>
  );

  const selectAddressModal = (
    <Modal
      css={modal}
      title={"住所の選択"}
      body={selectAddressModalBody}
      zIndex={5}
      footerButtons={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            キャンセル
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => {
              if (checkedAddressIndex !== null) {
                onConfirm(potentialAddresses[checkedAddressIndex]);
                onClose();
              } else {
                setNotSelected(true);
              }
            }}
          >
            決定
          </Button>
        </>
      }
      onClose={onClose}
      backgroundColor={color.backgroundGray}
    />
  );

  if (postalCode == null) {
    return <></>;
  }

  if (loading) {
    return <Loader css={{ zIndex: 9999 }}>処理中</Loader>;
  }

  if (isNotFoundError) {
    return errorModal(
      `入力された郵便番号に該当する住所が見つかりませんでした。\n郵便番号を変更して再度検索するか、住所を手入力してください。`
    );
  }

  if (isApiError) {
    return errorModal(
      `一時的なエラーにより住所の検索ができませんでした。\nもう一度検索いただくか、住所を手入力してください。`
    );
  }

  if (potentialAddresses && potentialAddresses.length > 1) {
    return selectAddressModal;
  }

  return <></>;
};

const modal = css({
  width: "464px",
  padding: "24px",
});

const radioButtonList = css({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});
